import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { ConfirmDialog, formatDayMonth } from '@comforthub/design-system'
import {
  buildAssistantUiContext,
  formatAssistantContextSummary,
} from '../../lib/assistantUiContext'
import {
  AlertCircle,
  Cpu,
  History,
  Loader2,
  Mic,
  MoreHorizontal,
  PanelLeftClose,
  Paperclip,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RotateCw,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  assistantChat,
  assistantConfirm,
  assistantConfirmAll,
  assistantDeleteSession,
  assistantListSessions,
  assistantPinSession,
  assistantRenameSession,
  assistantSessionMessages,
  assistantUploadImage,
  assistantUsage,
  mapSessionMessagesToChat,
  type ActionProposal,
  type AssistantSession,
  type ChatResponse,
  type DeepLink,
  type TableBlock,
  type UsageSnapshot,
} from '../../lib/assistantApi'
import { apiErrorMessage } from '../../lib/apiError'
import {
  assistantErrorMessage,
  assistantQuotaMessage,
  assistantScope,
  usageBlocks,
} from '../../lib/assistantAccess'
import { CONFIRM_ALL_PROGRESS_ID } from './AssistantMessageContent'
import {
  PROVIDER_BUSY_MESSAGE,
  providerBusyErrorDetails,
  providerBusyReplyDetails,
} from '../../lib/assistantProviderError'
import { ASSISTANT_SHORTCUT_LABEL } from '../../lib/assistantShortcut'
import {
  clearPendingAssistantGuide,
  consumePendingAssistantGuide,
  subscribeAssistantGuide,
  type AssistantGuideDetail,
} from '../../lib/assistantGuide'
import {
  AI_INTRO_BODY,
  AI_INTRO_TITLE,
  hasSeenAiIntro,
  markAiIntroSeen,
} from '../../lib/assistantIntro'
import {
  AI_CAPABILITY_PROMPTS,
  ASSISTANT_STARTER_CHIPS,
} from '../../lib/onboarding'
import {
  getSpeechRecognitionCtor,
  resolveDefaultSpeechLocale,
  SPEECH_LOCALES,
  type SpeechRecognitionLike,
} from '../../lib/speechInput'
import {
  invalidateAfterAssistantConfirm,
  notifyAssistantDataChanged,
} from '../../lib/assistantDataSync'
import AssistantMessageContent from './AssistantMessageContent'

type PendingImage = { id: string; file: File; previewUrl: string }

interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  proposals?: ActionProposal[]
  deepLinks?: DeepLink[]
  tables?: TableBlock[]
  /** A request that failed because the provider was busy: the raw text, behind Details. */
  providerError?: string
}

export interface AssistantPanelProps {
  /** `side`: the desktop panel beside the menu. `drawer`: over the page below 1024 px. */
  variant: 'side' | 'drawer'
  active: boolean
  /** Hides the AI: the last button in the header (X in the drawer). */
  onClose?: () => void
  closeOnNavigate?: boolean
  /** Bumped when the user opens the AI: focus goes to the message box. */
  focusToken?: number
  className?: string
}

type Screen = 'history' | 'chat'

const COUNT = new Intl.NumberFormat('en-US')

/** "12 of 10,000 today", or "Own key" when the company's own key pays (no allowance, ADR-W22). */
function formatQuota(usage: UsageSnapshot): string {
  if (usage.paidBy === 'company') return 'Own key'
  return `${COUNT.format(usage.promptsToday)} of ${COUNT.format(usage.promptsPerDayLimit)} today`
}

/** Amber from 80 % of the daily limit (TOU's allowance only). */
function quotaNearLimit(usage: UsageSnapshot): boolean {
  if (usage.paidBy === 'company') return false
  return usage.promptsPerDayLimit > 0 && usage.promptsToday / usage.promptsPerDayLimit >= 0.8
}

/** A sent user bubble back into its text and uploaded images (blob previews are gone). */
function parseUserMessage(content: string): { text: string; imageUrls: string[] } {
  const imageUrls: string[] = []
  const text = content
    .replace(/!\[attachment\]\(([^)\s]+)\)/g, (_m, url: string) => {
      if (!url.startsWith('blob:')) imageUrls.push(url)
      return ''
    })
    .trim()
  return { text, imageUrls }
}

/** The raw text when this message is a busy-provider failure, else null. */
function providerErrorDetails(msg: ChatMessage): string | null {
  if (msg.providerError != null) return msg.providerError
  if (msg.role !== 'assistant') return null
  return providerBusyReplyDetails(msg.content)
}

const ICON_BUTTON =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors'

function formatSessionTime(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = Date.now()
  const diffMs = now - d.getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return formatDayMonth(d)
}

export default function AssistantPanel({
  variant,
  active,
  onClose,
  closeOnNavigate = false,
  focusToken = 0,
  className = '',
}: AssistantPanelProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    companyId,
    companyName,
    companyRole,
    activePersona,
    activeRole,
    isPlatformAdmin,
    isOwner,
    permissions,
    impersonation,
  } = useAuth()
  // Platform AI in the Admin persona, the company's AI (which follows its package) otherwise.
  const scope = assistantScope(activePersona)
  const queryClient = useQueryClient()
  const [screen, setScreen] = useState<Screen>('chat')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('assistantSessionId') : null,
  )
  const [sessionTitle, setSessionTitle] = useState('New chat')
  const [sessionPinned, setSessionPinned] = useState(false)
  const [sessions, setSessions] = useState<AssistantSession[]>([])
  // The chat a Delete click is asking about, and whether its delete is running.
  const [chatToDelete, setChatToDelete] = useState<AssistantSession | null>(null)
  const [deletingChat, setDeletingChat] = useState(false)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [hydrating, setHydrating] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [provider, setProvider] = useState<string>('gemini')
  const [busy, setBusy] = useState(false)
  const [confirmingProposalId, setConfirmingProposalId] = useState<string | null>(null)
  const [confirmProgressLabel, setConfirmProgressLabel] = useState<string | null>(null)
  const confirmStartedAtRef = useRef<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [usage, setUsage] = useState<UsageSnapshot | null>(null)
  const [quotaBlocked, setQuotaBlocked] = useState(false)
  const [speechLocale, setSpeechLocale] = useState(() => resolveDefaultSpeechLocale().code)
  const [listening, setListening] = useState(false)
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([])
  const [optionsOpen, setOptionsOpen] = useState(false)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const hydratedRef = useRef<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const guideHandledRef = useRef<string | null>(null)
  // The last message sent, for Retry after a busy provider.
  const lastSentRef = useRef<{ text: string; imageUrls: string[] } | null>(null)
  const pendingFocusRef = useRef(false)
  const SpeechRecognitionCtor = getSpeechRecognitionCtor()
  const [introSeen, setIntroSeen] = useState(() => hasSeenAiIntro())

  const contextSummary = useMemo(() => {
    const uiContext = buildAssistantUiContext({
      pathname: location.pathname,
      search: location.search,
      companyId,
      companyName,
      companyRole,
      activePersona: activePersona === 'None' ? null : String(activePersona),
      activeRole,
      isPlatformAdmin,
      isOwner,
      permissions,
      impersonation,
    })
    return formatAssistantContextSummary(uiContext)
  }, [
    location.pathname,
    location.search,
    companyId,
    companyName,
    companyRole,
    activePersona,
    activeRole,
    isPlatformAdmin,
    isOwner,
    permissions,
    impersonation,
  ])

  const resizeComposer = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }, [])

  useEffect(() => {
    resizeComposer()
  }, [input, resizeComposer])

  // Opening the AI puts focus in the message box — once it can take it
  // (it is disabled while a reopened chat loads).
  useEffect(() => {
    if (focusToken > 0) pendingFocusRef.current = true
  }, [focusToken])
  useEffect(() => {
    if (!pendingFocusRef.current) return
    if (screen !== 'chat') {
      pendingFocusRef.current = false
      return
    }
    const el = inputRef.current
    if (!el || el.disabled) return
    el.focus()
    pendingFocusRef.current = false
  })

  const refreshSessions = useCallback(async () => {
    setSessionsLoading(true)
    try {
      const list = await assistantListSessions()
      setSessions(list)
    } catch {
      /* ignore */
    } finally {
      setSessionsLoading(false)
    }
  }, [])

  const hydrateSession = useCallback(async (id: string) => {
    setHydrating(true)
    setError(null)
    try {
      const res = await assistantSessionMessages(id)
      const mapped = mapSessionMessagesToChat(res.messages, res.pendingProposals ?? [])
      setMessages(mapped)
      setSessionId(id)
      setSessionTitle(res.title || 'New chat')
      setSessionPinned(Boolean(res.pinned))
      localStorage.setItem('assistantSessionId', id)
      hydratedRef.current = id
      setScreen('chat')
    } catch (e: any) {
      localStorage.removeItem('assistantSessionId')
      setSessionId(null)
      setMessages([])
      setSessionTitle('New chat')
      setSessionPinned(false)
      hydratedRef.current = null
      setScreen('history')
      setError(e?.response?.data?.message || e?.message || 'Could not load chat')
    } finally {
      setHydrating(false)
    }
  }, [])

  useEffect(() => {
    if (!active) return
    assistantUsage()
      .then((u) => {
        setUsage(u)
        if (u.preferredProvider) setProvider(u.preferredProvider)
        // Only hard-block when explicitly disabled or daily limit reached.
        // (Avoid treating missing `enabled` as blocked; provider ≠ quota.)
        setQuotaBlocked(usageBlocks(u))
      })
      .catch(() => {/* ignore */})
    void refreshSessions()
  }, [active, refreshSessions])

  useEffect(() => {
    if (!active) return
    if (screen !== 'chat') return
    if (!sessionId) {
      hydratedRef.current = null
      return
    }
    if (hydratedRef.current === sessionId && messages.length > 0) return
    void hydrateSession(sessionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, screen, sessionId])

  useEffect(() => {
    if (screen !== 'chat') return
    // Scroll only the message list. scrollIntoView() also scrolls every
    // ancestor (the overflow-hidden app shell and the page), which pushed the
    // whole backoffice up and left a grey empty area below it.
    const el = messagesScrollRef.current
    if (!el) return
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    } else {
      el.scrollTop = el.scrollHeight
    }
  }, [messages, active, busy, screen, confirmProgressLabel])

  useEffect(() => {
    if (!confirmingProposalId) return
    const tick = () => {
      const started = confirmStartedAtRef.current
      if (!started) return
      const secs = Math.max(0, Math.floor((Date.now() - started) / 1000))
      setConfirmProgressLabel((prev) => {
        const base = prev?.replace(/\s*\(\d+s\)$/, '') ?? 'Working on it — this can take a minute'
        return secs > 0 ? `${base} (${secs}s)` : base
      })
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [confirmingProposalId])

  const openHistory = () => {
    setScreen('history')
    setEditingTitle(false)
    setOptionsOpen(false)
    void refreshSessions()
  }

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      for (const img of prev) URL.revokeObjectURL(img.previewUrl)
      return []
    })
  }, [])

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop()
    } catch {
      /* ignore */
    }
    recognitionRef.current = null
    setListening(false)
  }, [])

  useEffect(() => {
    return () => {
      stopListening()
      clearPendingImages()
    }
  }, [stopListening, clearPendingImages])

  const newChat = () => {
    localStorage.removeItem('assistantSessionId')
    setSessionId(null)
    setMessages([])
    setSessionTitle('New chat')
    setSessionPinned(false)
    setError(null)
    setEditingTitle(false)
    setOptionsOpen(false)
    hydratedRef.current = null
    lastSentRef.current = null
    stopListening()
    clearPendingImages()
    setScreen('chat')
  }

  const selectSession = (id: string) => {
    hydratedRef.current = null
    setMessages([])
    setSessionId(id)
    localStorage.setItem('assistantSessionId', id)
    setScreen('chat')
  }

  const sendMessage = useCallback(
    async (
      rawText: string,
      imagesOverride?: PendingImage[],
      uploadedImageUrls: string[] = [],
    ): Promise<string | null> => {
      const text = rawText.trim()
      const images = imagesOverride ?? pendingImages
      if ((!text && images.length === 0 && uploadedImageUrls.length === 0) || busy || quotaBlocked) {
        return null
      }
      stopListening()
      setInput('')
      setPendingImages([])
      setError(null)
      markAiIntroSeen()
      setIntroSeen(true)
      const attachmentUrls = [...uploadedImageUrls, ...images.map((img) => img.previewUrl)]
      const optimistic =
        text +
        (attachmentUrls.length
          ? (text ? '\n\n' : '') + attachmentUrls.map((url) => `![attachment](${url})`).join('\n')
          : '')
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'user', content: optimistic }])
      setBusy(true)
      try {
        const imageUrls: string[] = [...uploadedImageUrls]
        for (const img of images) {
          imageUrls.push(await assistantUploadImage(img.file))
          URL.revokeObjectURL(img.previewUrl)
        }
        const message =
          text ||
          (imageUrls.length ? 'Please look at the attached image(s).' : '')
        lastSentRef.current = { text, imageUrls }
        const uiContext = buildAssistantUiContext({
          pathname: location.pathname,
          search: location.search,
          companyId,
          companyName,
          companyRole,
          activePersona: activePersona === 'None' ? null : String(activePersona),
          activeRole,
          isPlatformAdmin,
          isOwner,
          permissions,
          impersonation,
        })
        const res: ChatResponse = await assistantChat({
          sessionId,
          message,
          provider,
          imageUrls: imageUrls.length ? imageUrls : undefined,
          uiContext,
          scope,
        })
        setSessionId(res.sessionId)
        localStorage.setItem('assistantSessionId', res.sessionId)
        hydratedRef.current = res.sessionId
        if (res.title) setSessionTitle(res.title)
        setUsage(res.usage)
        setQuotaBlocked(usageBlocks(res.usage))
        // Replace optimistic user bubble URLs with uploaded URLs when present.
        if (imageUrls.length) {
          const finalUser =
            message +
            '\n\n' +
            imageUrls.map((url) => `![attachment](${url})`).join('\n')
          setMessages((m) => {
            const copy = [...m]
            for (let i = copy.length - 1; i >= 0; i--) {
              if (copy[i].role === 'user') {
                copy[i] = { ...copy[i], content: finalUser }
                break
              }
            }
            return [
              ...copy,
              {
                id: crypto.randomUUID(),
                role: 'assistant',
                content: res.reply,
                proposals: res.proposals,
                deepLinks: res.deepLinks,
                tables: res.tables,
              },
            ]
          })
        } else {
          setMessages((m) => [
            ...m,
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              content: res.reply,
              proposals: res.proposals,
              deepLinks: res.deepLinks,
              tables: res.tables,
            },
          ])
        }
        void refreshSessions()
        return res.sessionId
      } catch (e: any) {
        for (const img of images) URL.revokeObjectURL(img.previewUrl)
        const data = e?.response?.data
        const busyDetails = providerBusyErrorDetails(e)
        if (data?.error === 'assistant_quota_exceeded') {
          // A per-minute stop clears itself; the daily / monthly allowance blocks the composer.
          if (data.limit !== 'prompts_per_minute') setQuotaBlocked(true)
          setError(assistantQuotaMessage(data))
        } else if (busyDetails) {
          // In the thread, with Retry — not as a raw error under it.
          setMessages((m) => [
            ...m,
            { id: crypto.randomUUID(), role: 'assistant', content: PROVIDER_BUSY_MESSAGE, providerError: busyDetails },
          ])
        } else {
          setError(
            assistantErrorMessage(e, 'Assistant request failed', {
              canManageKeys: !!permissions?.includes('assistant:keys') || isPlatformAdmin,
            }),
          )
        }
        return null
      } finally {
        setBusy(false)
      }
    },
    [
      busy,
      pendingImages,
      provider,
      quotaBlocked,
      refreshSessions,
      sessionId,
      stopListening,
      location.pathname,
      location.search,
      companyId,
      companyName,
      companyRole,
      activePersona,
      activeRole,
      isPlatformAdmin,
      isOwner,
      permissions,
      impersonation,
    ],
  )

  const send = async () => {
    await sendMessage(input, pendingImages)
  }

  /** Retry after a busy provider: drop the failed reply and its question, then ask again. */
  const retryLast = () => {
    if (busy || quotaBlocked) return
    let lastUser: ChatMessage | undefined
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUser = messages[i]
        break
      }
    }
    const again = lastSentRef.current ?? (lastUser ? parseUserMessage(lastUser.content) : null)
    if (!again || (!again.text && again.imageUrls.length === 0)) return
    setMessages((m) => {
      const copy = [...m]
      while (copy.length && providerErrorDetails(copy[copy.length - 1]) != null) copy.pop()
      if (copy.length && copy[copy.length - 1].role === 'user') copy.pop()
      return copy
    })
    void sendMessage(again.text, [], again.imageUrls)
  }

  const startRenameCurrent = () => {
    if (!sessionId) return
    setOptionsOpen(false)
    setTitleDraft(sessionTitle)
    setEditingTitle(true)
  }

  const hideButton = onClose ? (
    <button
      type="button"
      data-testid="assistant-hide"
      onClick={onClose}
      className={ICON_BUTTON}
      aria-label="Hide AI"
      title={`Hide AI (${ASSISTANT_SHORTCUT_LABEL})`}
    >
      {variant === 'drawer' ? <X size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
    </button>
  ) : null

  const newChatButton = (
    <button
      type="button"
      data-testid="assistant-new-chat"
      onClick={newChat}
      className={ICON_BUTTON}
      aria-label="New chat"
      title="New chat"
    >
      <Plus size={18} aria-hidden="true" />
    </button>
  )

  const applyGuide = useCallback(
    (detail: AssistantGuideDetail) => {
      const prompt = detail.prompt.trim()
      if (!prompt) return
      const dedupeKey = `${prompt}::${detail.autoSend ? '1' : '0'}`
      if (guideHandledRef.current === dedupeKey) return
      guideHandledRef.current = dedupeKey
      clearPendingAssistantGuide()
      setScreen('chat')
      setEditingTitle(false)
      setInput(prompt)
      if (detail.autoSend) {
        // Guided prompts start a fresh thread so hydrate cannot stomp the send.
        localStorage.removeItem('assistantSessionId')
        setSessionId(null)
        setMessages([])
        setSessionTitle(detail.title || 'New chat')
        setSessionPinned(false)
        hydratedRef.current = null
        void sendMessage(prompt, []).then((sid) => {
          // Guided prompts are long instructions; without an explicit title the
          // server auto-titles the chat from the prompt text and the history
          // fills with identical "Introduce yourself…" entries (F1, 2026-08-01).
          if (!sid || !detail.title) return
          assistantRenameSession(sid, detail.title)
            .then((updated) => {
              setSessionTitle(updated.title || detail.title!)
              void refreshSessions()
            })
            .catch(() => {})
        })
      }
    },
    [refreshSessions, sendMessage],
  )

  useEffect(() => {
    if (!active) return
    const pending = consumePendingAssistantGuide()
    if (pending) applyGuide(pending)
    return subscribeAssistantGuide((detail) => {
      // Fresh event while panel is open — allow re-apply.
      guideHandledRef.current = null
      applyGuide(detail)
    })
  }, [active, applyGuide])

  const toggleMic = () => {
    if (busy || quotaBlocked || hydrating) return
    if (listening) {
      stopListening()
      return
    }
    if (!SpeechRecognitionCtor) {
      setError('Speech recognition needs Chrome or Edge')
      return
    }
    const locale =
      SPEECH_LOCALES.find((l) => l.code === speechLocale) || resolveDefaultSpeechLocale()
    const recognition = new SpeechRecognitionCtor()
    recognition.lang = locale.stt
    recognition.interimResults = true
    recognition.continuous = false
    recognition.maxAlternatives = 1
    let finalText = ''
    recognition.onstart = () => setListening(true)
    recognition.onresult = (ev) => {
      let interim = ''
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript
        if (ev.results[i].isFinal) finalText += t
        else interim += t
      }
      const draft = (finalText || interim).trim()
      if (draft) setInput(draft)
    }
    recognition.onerror = (ev) => {
      setListening(false)
      recognitionRef.current = null
      if (ev.error === 'not-allowed') setError('Microphone permission denied')
      else if (ev.error !== 'aborted') setError(`Mic error: ${ev.error}`)
    }
    recognition.onend = () => {
      setListening(false)
      recognitionRef.current = null
      // Draft only — never auto-send. User must press Send.
      const draft = finalText.trim()
      if (draft) setInput((prev) => (prev.trim() === draft ? prev : draft))
    }
    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (e: any) {
      setListening(false)
      setError(e?.message || 'Could not start microphone')
    }
  }

  const onPickImages = (files: FileList | null) => {
    if (!files?.length) return
    const next: PendingImage[] = []
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are supported')
        continue
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image exceeds the 5 MB upload limit')
        continue
      }
      next.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
      })
    }
    if (next.length) {
      setPendingImages((prev) => [...prev, ...next].slice(0, 4))
      setError(null)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const keep: PendingImage[] = []
      for (const img of prev) {
        if (img.id === id) URL.revokeObjectURL(img.previewUrl)
        else keep.push(img)
      }
      return keep
    })
  }

  const onConfirm = async (proposal: ActionProposal, confirm: boolean) => {
    setBusy(true)
    setError(null)
    setConfirmingProposalId(proposal.id)
    confirmStartedAtRef.current = Date.now()
    const progressBase =
      confirm && proposal.toolName === 'proposePublishMonthSchedule'
        ? 'Publishing shifts — this can take a minute'
        : confirm &&
            (proposal.toolName === 'proposeReassignShifts' ||
              proposal.toolName === 'proposeRedistributeHoursTowardNorm')
          ? 'Updating shift assignments — this can take a minute'
          : confirm
            ? 'Applying change — hang tight'
            : 'Rejecting…'
    setConfirmProgressLabel(progressBase)
    try {
      const res = await assistantConfirm(proposal.id, confirm, scope)
      const deepLinks = confirm ? res.deepLinks ?? [] : []
      const friendlySummary =
        confirm && res.resultSummary && res.resultSummary.trim().startsWith('{')
          ? proposal.summary
          : res.resultSummary ?? proposal.summary
      setMessages((m) =>
        m
          .map((msg) =>
            msg.proposals?.some((p) => p.id === proposal.id)
              ? { ...msg, proposals: msg.proposals.filter((p) => p.id !== proposal.id) }
              : msg,
          )
          .concat({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: confirm ? `Confirmed: ${friendlySummary}` : `Rejected: ${proposal.summary}`,
            deepLinks: deepLinks.length ? deepLinks : undefined,
          }),
      )
      if (confirm) {
        invalidateAfterAssistantConfirm(queryClient, proposal.toolName)
        notifyAssistantDataChanged({
          confirmed: true,
          toolName: proposal.toolName,
          args: proposal.args,
          resultSummary: res?.resultSummary ?? null,
        })
        const route = deepLinks[0]?.route
        if (route) {
          navigate(route)
          if (closeOnNavigate && onClose) onClose()
        }
      } else {
        notifyAssistantDataChanged({
          confirmed: false,
          toolName: proposal.toolName,
          args: proposal.args,
          resultSummary: res?.resultSummary ?? null,
        })
      }
    } catch (e: any) {
      const timedOut =
        e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message ?? ''))
      setError(
        timedOut
          ? 'Confirm is taking longer than expected. Check Shifts — the publish may still finish in the background.'
          : assistantErrorMessage(e, 'Confirm failed'),
      )
    } finally {
      setBusy(false)
      setConfirmingProposalId(null)
      setConfirmProgressLabel(null)
      confirmStartedAtRef.current = null
    }
  }

  /**
   * Confirm/reject every proposal visible on a message in one click.
   * Uses POST /assistant/confirm-all (server sequential, per-item results).
   * Progress: "Confirming i/n…" via sequential confirms when the batch endpoint
   * is unavailable; otherwise "Confirming n proposals…" with elapsed seconds.
   *
   * Scope: proposals on the current chat message (session pending cards are
   * attached to the latest assistant message on hydrate).
   */
  const onConfirmAll = async (proposals: ActionProposal[], confirm: boolean) => {
    if (!proposals.length) return
    const total = proposals.length
    const byId = new Map(proposals.map((p) => [p.id, p]))
    setBusy(true)
    setError(null)
    setConfirmingProposalId(CONFIRM_ALL_PROGRESS_ID)
    confirmStartedAtRef.current = Date.now()
    setConfirmProgressLabel(
      confirm
        ? `Confirming ${total} proposals — this can take a minute`
        : `Rejecting ${total} proposals…`,
    )
    try {
      let results: Array<{
        proposalId: string
        status: string
        resultSummary?: string | null
        deepLinks?: DeepLink[] | null
      }>
      let confirmed = 0
      let rejected = 0
      let failed = 0
      try {
        const batch = await assistantConfirmAll(
          proposals.map((p) => p.id),
          confirm,
          scope,
        )
        results = batch.results ?? []
        confirmed = batch.confirmed
        rejected = batch.rejected
        failed = batch.failed
      } catch (batchErr: any) {
        // Older cores without /confirm-all — sequential with live i/n.
        if (batchErr?.response?.status !== 404 && batchErr?.response?.status !== 405) {
          throw batchErr
        }
        results = []
        for (let i = 0; i < proposals.length; i++) {
          const p = proposals[i]
          setConfirmProgressLabel(
            confirm ? `Confirming ${i + 1}/${total}…` : `Rejecting ${i + 1}/${total}…`,
          )
          try {
            const one = await assistantConfirm(p.id, confirm, scope)
            results.push(one)
            if (one.status === 'confirmed') confirmed++
            else if (one.status === 'rejected') rejected++
            else failed++
          } catch (e: any) {
            failed++
            results.push({
              proposalId: p.id,
              status: 'failed',
              resultSummary: assistantErrorMessage(e, 'Confirm failed'),
            })
          }
        }
      }
      const resolvedIds = new Set(
        results
          .filter((r) => r.status === 'confirmed' || r.status === 'rejected')
          .map((r) => r.proposalId),
      )
      const deepLinks: DeepLink[] = []
      const seenRoutes = new Set<string>()
      for (const r of results) {
        if (r.status !== 'confirmed' && r.status !== 'rejected') continue
        const proposal = byId.get(r.proposalId)
        if (proposal) {
          if (confirm) {
            invalidateAfterAssistantConfirm(queryClient, proposal.toolName)
          }
          notifyAssistantDataChanged({
            confirmed: confirm,
            toolName: proposal.toolName,
            args: proposal.args,
            resultSummary: r.resultSummary ?? null,
          })
        }
        for (const link of r.deepLinks ?? []) {
          if (!link?.route || seenRoutes.has(link.route)) continue
          seenRoutes.add(link.route)
          deepLinks.push(link)
        }
      }
      const ok = confirm ? confirmed : rejected
      const summary = confirm
        ? failed > 0
          ? `Confirmed ${ok}/${total} actions (${failed} failed).`
          : `Confirmed all ${ok} actions.`
        : failed > 0
          ? `Rejected ${ok}/${total} actions (${failed} failed).`
          : `Rejected all ${ok} actions.`
      setMessages((m) =>
        m
          .map((msg) =>
            msg.proposals?.some((p) => resolvedIds.has(p.id))
              ? {
                  ...msg,
                  proposals: msg.proposals.filter((p) => !resolvedIds.has(p.id)),
                }
              : msg,
          )
          .concat({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: summary,
            deepLinks: deepLinks.length ? deepLinks : undefined,
          }),
      )
      if (failed > 0) {
        const firstFail = results.find((r) => r.status === 'failed')
        setError(firstFail?.resultSummary || `${failed} action(s) could not be applied`)
      }
      if (confirm && deepLinks[0]?.route) {
        navigate(deepLinks[0].route)
        if (closeOnNavigate && onClose) onClose()
      }
    } catch (e: any) {
      const timedOut =
        e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message ?? ''))
      setError(
        timedOut
          ? 'Confirm all is taking longer than expected. Check Shifts — some changes may still finish in the background.'
          : assistantErrorMessage(e, 'Confirm all failed'),
      )
    } finally {
      setBusy(false)
      setConfirmingProposalId(null)
      setConfirmProgressLabel(null)
      confirmStartedAtRef.current = null
    }
  }

  const saveHeaderTitle = async () => {
    if (!sessionId || !titleDraft.trim()) {
      setEditingTitle(false)
      return
    }
    try {
      const updated = await assistantRenameSession(sessionId, titleDraft.trim())
      setSessionTitle(updated.title || titleDraft.trim())
      setEditingTitle(false)
      void refreshSessions()
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Rename failed')
    }
  }

  const togglePinCurrent = async () => {
    if (!sessionId) return
    try {
      const updated = await assistantPinSession(sessionId, !sessionPinned)
      setSessionPinned(Boolean(updated.pinned))
      void refreshSessions()
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Pin failed')
    }
  }

  const togglePinInList = async (s: AssistantSession) => {
    try {
      await assistantPinSession(s.id, !s.pinned)
      void refreshSessions()
      if (s.id === sessionId) setSessionPinned(!s.pinned)
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Pin failed')
    }
  }

  const startRenameInList = (s: AssistantSession) => {
    setRenamingId(s.id)
    setRenameValue(s.title || '')
  }

  const submitRenameInList = async () => {
    if (!renamingId || !renameValue.trim()) {
      setRenamingId(null)
      return
    }
    try {
      const updated = await assistantRenameSession(renamingId, renameValue.trim())
      if (renamingId === sessionId) setSessionTitle(updated.title || renameValue.trim())
      setRenamingId(null)
      void refreshSessions()
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Rename failed')
    }
  }

  // Waits for the server before closing; a failure shows in the error line under the list.
  const deleteSession = async (id: string) => {
    setDeletingChat(true)
    try {
      await assistantDeleteSession(id)
      if (sessionId === id) {
        newChat()
        setScreen('history')
      }
      void refreshSessions()
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Delete failed'))
    } finally {
      setDeletingChat(false)
      setChatToDelete(null)
    }
  }

  const handleDeepLink = (route: string) => {
    navigate(route)
    if (closeOnNavigate) onClose?.()
  }

  const pinnedSessions = sessions.filter((s) => s.pinned)
  const otherSessions = sessions.filter((s) => !s.pinned)

  if (screen === 'history') {
    return (
      <div
        className={`h-full bg-surface flex flex-col ${className}`}
        data-testid="assistant-history-screen"
        data-assistant-panel=""
      >
        <header className="flex items-center gap-1.5 pl-3 pr-2.5 py-3 border-b border-border shrink-0">
          <History size={18} className="text-accent shrink-0 mx-1.5" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-sm">Chats</h2>
            <p className="text-[11px] text-foreground-muted truncate">
              {sessions.length} conversation{sessions.length === 1 ? '' : 's'}
            </p>
          </div>
          {newChatButton}
          {hideButton}
        </header>

        <div className="flex-1 overflow-y-auto min-h-0" data-testid="assistant-session-list">
          {sessionsLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-foreground-muted">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-foreground-muted space-y-3 pt-12 px-6 text-center">
              <Sparkles className="mx-auto text-accent" size={28} aria-hidden="true" />
              <p>No chats yet.</p>
              <button
                type="button"
                onClick={newChat}
                className="text-xs font-semibold text-accent"
              >
                Start a new chat
              </button>
            </div>
          ) : (
            <div className="py-1">
              {pinnedSessions.length > 0 ? (
                <section>
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-foreground-muted font-semibold">
                    Pinned
                  </p>
                  <ul>
                    {pinnedSessions.map((s) => (
                      <SessionRow
                        key={s.id}
                        session={s}
                        active={s.id === sessionId}
                        renamingId={renamingId}
                        renameValue={renameValue}
                        onRenameValue={setRenameValue}
                        onSelect={() => selectSession(s.id)}
                        onStartRename={() => startRenameInList(s)}
                        onSubmitRename={() => void submitRenameInList()}
                        onCancelRename={() => setRenamingId(null)}
                        onTogglePin={() => void togglePinInList(s)}
                        onDelete={() => setChatToDelete(s)}
                      />
                    ))}
                  </ul>
                </section>
              ) : null}
              <section>
                {pinnedSessions.length > 0 ? (
                  <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wide text-foreground-muted font-semibold">
                    Recent
                  </p>
                ) : null}
                <ul>
                  {otherSessions.map((s) => (
                    <SessionRow
                      key={s.id}
                      session={s}
                      active={s.id === sessionId}
                      renamingId={renamingId}
                      renameValue={renameValue}
                      onRenameValue={setRenameValue}
                      onSelect={() => selectSession(s.id)}
                      onStartRename={() => startRenameInList(s)}
                      onSubmitRename={() => void submitRenameInList()}
                      onCancelRename={() => setRenamingId(null)}
                      onTogglePin={() => void togglePinInList(s)}
                      onDelete={() => setChatToDelete(s)}
                    />
                  ))}
                </ul>
              </section>
            </div>
          )}
        </div>

        {error ? (
          <div role="alert" className="px-4 py-2 text-xs text-rose-500 border-t border-border shrink-0">{error}</div>
        ) : null}

        {chatToDelete ? (
          <ConfirmDialog
            open
            tone="danger"
            title={`Delete the chat “${chatToDelete.title || 'New chat'}”?`}
            confirmLabel="Delete chat"
            pending={deletingChat}
            onConfirm={() => void deleteSession(chatToDelete.id)}
            onCancel={() => setChatToDelete(null)}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={`h-full bg-surface flex flex-col ${className}`}
      data-testid="assistant-chat-screen"
      data-assistant-panel=""
    >
      <header className="flex items-center gap-1.5 pl-3 pr-2.5 py-3 border-b border-border/80 shrink-0 bg-surface/95">
        <button
          type="button"
          data-testid="assistant-history-toggle"
          onClick={openHistory}
          className={ICON_BUTTON}
          aria-label="All chats"
          title="All chats"
        >
          <History size={18} aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1 pl-0.5">
          {editingTitle && sessionId ? (
            <input
              autoFocus
              data-testid="assistant-title-input"
              aria-label="Chat name"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void saveHeaderTitle()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void saveHeaderTitle()
                }
                if (e.key === 'Escape') setEditingTitle(false)
              }}
              className="w-full text-sm font-semibold rounded-lg border border-border bg-transparent px-2 py-1"
            />
          ) : (
            <button
              type="button"
              data-testid="assistant-title"
              onClick={startRenameCurrent}
              className="block w-full text-left font-semibold text-sm truncate hover:text-accent transition-colors"
              title={sessionId ? 'Rename chat' : sessionTitle}
            >
              {sessionTitle}
            </button>
          )}
          {usage ? (
            <p
              data-testid="assistant-quota"
              className={`text-[11px] truncate mt-0.5 ${
                quotaNearLimit(usage) ? 'text-amber-500 font-medium' : 'text-foreground-muted'
              }`}
              title={
                usage.ratePlanName || usage.ratePlanCode
                  ? `Rate plan: ${usage.ratePlanName ?? usage.ratePlanCode}`
                  : undefined
              }
            >
              {formatQuota(usage)}
            </p>
          ) : null}
        </div>
        {newChatButton}
        <div className="relative">
          <button
            type="button"
            data-testid="assistant-options-toggle"
            onClick={() => setOptionsOpen((o) => !o)}
            className={ICON_BUTTON}
            aria-label="Chat options"
            title="More: pin, rename, AI provider, mic language"
            aria-haspopup="true"
            aria-expanded={optionsOpen}
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </button>
          {optionsOpen ? (
            <>
              <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOptionsOpen(false)} />
              <div
                role="group"
                aria-label="Chat options"
                data-testid="assistant-chat-options"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation()
                    setOptionsOpen(false)
                  }
                }}
                className="absolute right-0 top-full mt-1 z-50 w-64 rounded-xl border border-border bg-surface shadow-lg p-1.5 animate-in fade-in slide-in-from-top-1 duration-100"
              >
                <button
                  type="button"
                  disabled={!sessionId}
                  onClick={() => {
                    setOptionsOpen(false)
                    void togglePinCurrent()
                  }}
                  className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-foreground hover:bg-surface-hover disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  {sessionPinned ? (
                    <PinOff size={15} className="text-foreground-muted" aria-hidden="true" />
                  ) : (
                    <Pin size={15} className="text-foreground-muted" aria-hidden="true" />
                  )}
                  {sessionPinned ? 'Unpin chat' : 'Pin chat'}
                </button>
                <button
                  type="button"
                  disabled={!sessionId}
                  onClick={startRenameCurrent}
                  className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-foreground hover:bg-surface-hover disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  <Pencil size={15} className="text-foreground-muted" aria-hidden="true" />
                  Rename
                </button>
                <div className="my-1 mx-1 h-px bg-border" />
                <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] text-foreground">
                  <Cpu size={15} className="text-foreground-muted shrink-0" aria-hidden="true" />
                  <span className="flex-1">AI provider</span>
                  <select
                    data-testid="assistant-provider"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className="text-xs rounded-lg border border-border bg-surface px-2 py-1"
                  >
                    <option value="gemini">Gemini</option>
                    <option value="claude">Claude</option>
                  </select>
                </label>
                <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] text-foreground">
                  <Mic size={15} className="text-foreground-muted shrink-0" aria-hidden="true" />
                  <span className="flex-1">Mic language</span>
                  <select
                    data-testid="assistant-speech-locale"
                    value={speechLocale}
                    onChange={(e) => setSpeechLocale(e.target.value)}
                    className="text-xs rounded-lg border border-border bg-surface px-2 py-1 max-w-[7.5rem]"
                  >
                    {SPEECH_LOCALES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          ) : null}
        </div>
        {hideButton}
      </header>

      <div data-testid="assistant-context-strip" className="px-3.5 py-2 border-b border-border/60 shrink-0">
        <span
          className="inline-flex max-w-full items-center rounded-full border border-border bg-surface-raised/60 px-2.5 py-0.5 text-[11.5px] text-foreground-muted"
          title={contextSummary.details || undefined}
        >
          <span className="truncate">{contextSummary.primary}</span>
        </span>
      </div>

      <div
        ref={messagesScrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0"
        data-testid="assistant-messages"
      >
        {hydrating ? (
          <div className="flex items-center justify-center gap-2 pt-12 text-sm text-foreground-muted">
            <Loader2 size={16} className="animate-spin" /> Loading chat…
          </div>
        ) : null}
        {!hydrating && messages.length === 0 ? (
          <div
            className="text-sm text-foreground-muted space-y-4 pt-8 text-center animate-in fade-in duration-300"
            data-testid="assistant-empty-state"
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent">
              <Sparkles size={26} aria-hidden="true" />
            </div>
            {!introSeen ? (
              <div className="space-y-2.5 text-left rounded-2xl border border-border/80 bg-surface-raised/70 px-4 py-4 shadow-sm">
                <p className="font-semibold text-foreground text-sm text-center">{AI_INTRO_TITLE}</p>
                <p className="text-xs leading-relaxed text-foreground-muted">{AI_INTRO_BODY}</p>
                <p className="text-[11px] text-foreground-muted/90 text-center">
                  Writes need your confirmation. The first message names this chat.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5 px-1">
                <p className="text-foreground font-medium text-[15px]">How can I help?</p>
                <p className="text-xs leading-relaxed max-w-[18rem] mx-auto">
                  Orders, stock, stores, shifts, plans, team invites, or navigating the backoffice.
                </p>
                <p className="text-[11px] text-foreground-muted/90">
                  Writes need your confirmation.
                </p>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2 pt-1" data-testid="assistant-starter-chips">
              {ASSISTANT_STARTER_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  disabled={busy || quotaBlocked}
                  onClick={() => {
                    const prompt = AI_CAPABILITY_PROMPTS[chip.promptKey]
                    guideHandledRef.current = null
                    applyGuide({ prompt, autoSend: true, title: chip.label })
                  }}
                  className="rounded-full border border-border/80 bg-surface px-3 py-1.5 text-xs font-semibold text-foreground transition-all hover:border-accent/40 hover:bg-accent/10 hover:text-accent disabled:opacity-50"
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {!hydrating &&
          messages.map((msg, index) => {
            const busyDetails = providerErrorDetails(msg)
            return (
              <div
                key={msg.id}
                className={`flex gap-2.5 animate-in fade-in slide-in-from-bottom-1 duration-200 ${
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {msg.role !== 'user' ? (
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Sparkles size={14} aria-hidden="true" />
                  </div>
                ) : null}
                {busyDetails != null ? (
                  <ProviderBusyBubble
                    details={busyDetails}
                    onRetry={index === messages.length - 1 ? retryLast : undefined}
                    retryDisabled={busy || quotaBlocked}
                  />
                ) : (
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-accent/15 text-foreground whitespace-pre-wrap rounded-br-md'
                        : 'bg-surface-raised border border-border/60 text-foreground rounded-bl-md shadow-sm'
                    }`}
                  >
                    <AssistantMessageContent
                      role={msg.role}
                      content={msg.content}
                      proposals={msg.proposals}
                      deepLinks={msg.deepLinks}
                      tables={msg.tables}
                      busy={busy}
                      confirmingProposalId={confirmingProposalId}
                      confirmProgressLabel={confirmProgressLabel}
                      onConfirm={(proposal, confirm) => void onConfirm(proposal, confirm)}
                      onConfirmAll={(list, confirm) => void onConfirmAll(list, confirm)}
                      onDeepLink={handleDeepLink}
                    />
                  </div>
                )}
              </div>
            )
          })}
        {busy &&
        messages.length > 0 &&
        (messages[messages.length - 1]?.role === 'user' ||
          confirmingProposalId != null) ? (
          <div className="flex items-center gap-2.5 animate-in fade-in duration-200">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
              <Sparkles size={14} aria-hidden="true" />
            </div>
            <div
              className="inline-flex flex-col gap-1 rounded-2xl rounded-bl-md border border-border/60 bg-surface-raised px-3.5 py-2.5 shadow-sm"
              aria-label={confirmingProposalId ? 'Confirming' : 'Thinking'}
              data-testid="assistant-busy-indicator"
            >
              <div className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-foreground-muted/70 animate-pulse" />
                <span className="h-1.5 w-1.5 rounded-full bg-foreground-muted/70 animate-pulse [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-foreground-muted/70 animate-pulse [animation-delay:300ms]" />
              </div>
              {confirmProgressLabel ? (
                <span className="text-[11px] text-foreground-muted">{confirmProgressLabel}</span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="px-5 py-2.5 text-xs text-rose-500 border-t border-border shrink-0 bg-rose-500/5">
          {error}
        </div>
      ) : null}

      <footer className="border-t border-border/80 p-3.5 space-y-2.5 shrink-0 bg-surface">
        {pendingImages.length > 0 ? (
          <div className="flex gap-2 flex-wrap px-0.5" data-testid="assistant-pending-images">
            {pendingImages.map((img) => (
              <div key={img.id} className="relative w-14 h-14 rounded-xl overflow-hidden border border-border">
                <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  aria-label="Remove image"
                  title="Remove image"
                  onClick={() => removePendingImage(img.id)}
                  className="absolute top-0.5 right-0.5 rounded-full bg-black/60 text-white p-0.5"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="rounded-2xl border border-border/80 bg-surface-raised/50 p-2 shadow-sm">
          <div className="flex gap-1.5 items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              data-testid="assistant-file-input"
              onChange={(e) => onPickImages(e.target.files)}
            />
            <button
              type="button"
              data-testid="assistant-attach"
              aria-label="Attach image"
              title="Attach image"
              disabled={busy || quotaBlocked || hydrating}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl p-2.5 text-foreground-muted hover:bg-surface-hover hover:text-foreground disabled:opacity-50 transition-colors"
            >
              <Paperclip size={16} />
            </button>
            <button
              type="button"
              data-testid="assistant-mic"
              aria-label={listening ? 'Stop listening' : 'Speak'}
              title={listening ? 'Stop listening' : 'Talk — text fills the box; press Send to confirm'}
              disabled={busy || quotaBlocked || hydrating || !SpeechRecognitionCtor}
              onClick={toggleMic}
              className={`rounded-xl p-2.5 disabled:opacity-50 transition-colors ${
                listening
                  ? 'bg-rose-500/15 text-rose-600'
                  : 'text-foreground-muted hover:bg-surface-hover hover:text-foreground'
              }`}
            >
              <Mic size={16} />
            </button>
            <textarea
              ref={inputRef}
              data-testid="assistant-input"
              aria-label="Message to AI"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void send()
                }
              }}
              disabled={busy || quotaBlocked || hydrating}
              placeholder={
                quotaBlocked
                  ? usage?.enabled === false
                    ? 'Assistant disabled for this company'
                    : 'Daily quota reached'
                  : listening
                    ? 'Listening… edit then Send'
                    : 'Ask anything…'
              }
              className="flex-1 min-h-[40px] max-h-[140px] resize-none text-sm leading-relaxed rounded-xl border-0 bg-transparent px-2.5 py-2.5 focus:ring-0 placeholder:text-foreground-muted/70"
            />
            <button
              type="button"
              data-testid="assistant-send"
              aria-label="Send message"
              title="Send message"
              disabled={
                busy || quotaBlocked || hydrating || (!input.trim() && pendingImages.length === 0)
              }
              onClick={() => void send()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-white shadow-sm transition-all hover:opacity-95 disabled:opacity-40 disabled:shadow-none"
            >
              {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}

/** A busy provider, in the thread: one plain sentence, Retry, and the raw text behind Details. */
function ProviderBusyBubble({
  details,
  onRetry,
  retryDisabled,
}: {
  details: string
  /** Only on the newest message: it asks the last question again. */
  onRetry?: () => void
  retryDisabled: boolean
}) {
  const [showDetails, setShowDetails] = useState(false)
  return (
    <div
      role="alert"
      data-testid="assistant-provider-busy"
      className="max-w-[85%] flex-1 rounded-2xl rounded-bl-md border border-rose-500/35 bg-rose-500/[0.08] px-3.5 py-3 text-sm space-y-2.5"
    >
      <p className="flex items-start gap-2 leading-relaxed text-foreground">
        <AlertCircle size={16} className="mt-0.5 shrink-0 text-rose-400" aria-hidden="true" />
        <span>{PROVIDER_BUSY_MESSAGE}</span>
      </p>
      <div className="flex items-center gap-2">
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={retryDisabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-surface-hover disabled:opacity-50"
          >
            <RotateCw size={13} aria-hidden="true" />
            Retry
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          className="rounded-lg px-2 py-1.5 text-xs text-foreground-muted hover:text-foreground hover:bg-surface-hover"
        >
          Details
        </button>
      </div>
      {showDetails ? (
        <pre
          data-testid="assistant-provider-busy-details"
          className="max-h-48 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-lg border border-border bg-surface p-2 font-mono text-[11px] text-foreground-muted"
        >
          {details}
        </pre>
      ) : null}
    </div>
  )
}

function SessionRow({
  session,
  active,
  renamingId,
  renameValue,
  onRenameValue,
  onSelect,
  onStartRename,
  onSubmitRename,
  onCancelRename,
  onTogglePin,
  onDelete,
}: {
  session: AssistantSession
  active: boolean
  renamingId: string | null
  renameValue: string
  onRenameValue: (v: string) => void
  onSelect: () => void
  onStartRename: () => void
  onSubmitRename: () => void
  onCancelRename: () => void
  onTogglePin: () => void
  onDelete: () => void
}) {
  return (
    <li
      className={`group flex items-center gap-1 px-2 py-2.5 mx-1 rounded-xl text-sm ${
        active ? 'bg-accent/10' : 'hover:bg-surface-hover'
      }`}
    >
      {renamingId === session.id ? (
        <input
          autoFocus
          value={renameValue}
          onChange={(e) => onRenameValue(e.target.value)}
          onBlur={onSubmitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onSubmitRename()
            }
            if (e.key === 'Escape') onCancelRename()
          }}
          className="flex-1 min-w-0 rounded border border-border bg-transparent px-2 py-1 text-sm"
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="flex-1 min-w-0 text-left"
        >
          <span className="block font-medium truncate">{session.title || 'New chat'}</span>
          <span className="block text-[11px] text-foreground-muted">
            {formatSessionTime(session.modifiedAt || session.createdAt)}
          </span>
        </button>
      )}
      <button
        type="button"
        aria-label={session.pinned ? 'Unpin chat' : 'Pin chat'}
        title={session.pinned ? 'Unpin' : 'Pin'}
        onClick={onTogglePin}
        className={`p-1.5 rounded-lg ${session.pinned ? 'text-accent' : 'text-foreground-muted opacity-0 group-hover:opacity-100'}`}
      >
        <Pin size={14} />
      </button>
      <button
        type="button"
        aria-label="Rename chat"
        title="Rename"
        onClick={onStartRename}
        className="p-1.5 rounded-lg text-foreground-muted opacity-0 group-hover:opacity-100 hover:text-accent"
      >
        <Pencil size={14} />
      </button>
      <button
        type="button"
        aria-label="Delete chat"
        title="Delete"
        onClick={onDelete}
        className="p-1.5 rounded-lg text-foreground-muted opacity-0 group-hover:opacity-100 hover:text-rose-500"
      >
        <Trash2 size={14} />
      </button>
    </li>
  )
}
